import "./trialPreferences.css";

import { Badge, Button, Collapse } from "reactstrap";
import { MAP_DISPLAY_MODES, NULL_GROUP_LABEL } from "../../../../constants";
import { memo, useCallback, useMemo, useState } from "react";
import {
  requestUpdateContractInfo,
  requestUpdateContractPreferences,
  requestUpdateSite,
  requestUpdateSitePreferences,
} from "../../../../services/backendRequests";
import {
  selectAttributesIncludingExperimentsAndModalities,
  selectEditedFieldBookColors,
  selectHasResults,
} from "../../../../selectors/resultMap";
import {
  setMapDisplayMode,
  setPendingFieldBookColors,
  setPlotMapColoring,
  setSelectedAttribute,
} from "../../../../actions/resultMap";
import { useDispatch, useSelector } from "react-redux";

import { ColorPicker } from "./ColorPicker";
import PropTypes from "prop-types";
import { PropertyName } from "../../../../components/PropertyName/PropertyName";
import { addDangerAlert } from "../../../../actions/alerts";
import { hasBackofficeAccess } from "../../../../services/roles";
import { selectTraitsIndexedOnTechnicalName } from "../../../../selectors/traits";
import { setContractPreferences } from "../../../../actions/user";
import { setTrial } from "../../../../actions/sites";
import { useAttributeColor } from "../../../../hooks/useAttributeColor";
import { useGetPropertyCategory } from "../../../../hooks/useGetPropertyCategory";

// Above this many distinct values, rendering one native color input per value
// floods the DOM; color editing stops being usable anyway. Cap it.
const MAX_EDITABLE_VALUES = 1000;
// Values a search may unfold at once (see shownAttributes).
const MAX_AUTO_OPENED_VALUES = 100;

// fieldBookColors is { category: { value: color } }: merging must go one level
// down, a shallow spread would drop the untouched values of an edited category.
const mergeColors = (base, extra) =>
  Object.entries(extra).reduce(
    (merged, [category, values]) => ({
      ...merged,
      [category]: { ...merged[category], ...values },
    }),
    { ...base }
  );

const AttributeSection = memo(
  ({
    category,
    values,
    isOpen,
    fieldBookColors,
    contractColors,
    isSaving,
    toggleSection,
    handleColorChange,
    handleRestoreDefault,
    totalCount,
    openedBySearch,
    onMap,
    onPlotMap,
  }) => {
    const dispatch = useDispatch();
    // Resolve against the pending edits, not the saved palette: after a restore
    // the row must fall back to the inherited/default colour right away.
    const getAttributeColor = useAttributeColor(category, fieldBookColors);
    // One selector per value: a returned array is a new reference on every
    // store update, which re-rendered every section despite memo().
    const selectedAttribute = useSelector(
      ({ resultMap }) => resultMap.selectedAttribute
    );
    const mapDisplayMode = useSelector(
      ({ resultMap }) => resultMap.mapDisplayMode
    );
    const plotMapColoring = useSelector(
      ({ resultMap }) => resultMap.plotMapColoring
    );
    // On the map the plots show either a trait or an attribute: this attribute
    // is the one on screen only in the field book mode, or in Plot Map once
    // its colouring is on. Without a map (PCA) nothing is on screen.
    const isDisplayed =
      selectedAttribute === category &&
      ((onMap && mapDisplayMode === MAP_DISPLAY_MODES.FIELD_BOOK) ||
        (onPlotMap && plotMapColoring));

    if (!values || values.length === 0) return null;

    // On the map the row displays the attribute: that is what people click it
    // for, unfolding the colours is the Edit button's job. Without a map there
    // is nothing to display, the row unfolds the colours as it used to.
    // Plot Map stays in its tab: the row toggles the colours on its plots.
    const onRowClick = () => {
      if (onMap) {
        dispatch(setSelectedAttribute(category));
      } else if (onPlotMap) {
        if (!isDisplayed) dispatch(setSelectedAttribute(category));
        dispatch(setPlotMapColoring(!isDisplayed));
      } else {
        toggleSection(category, openedBySearch);
      }
    };

    return (
      <div
        className={`fieldbook-category mb-1 ${
          isDisplayed ? "is-displayed" : ""
        }`}
        key={category}
      >
        {/* On the map the row is the display action; being on screen shows on
            the row itself (green rail and tint), no separate icon. */}
        <div
          className="fieldbook-category__head d-flex align-items-center gap-2 clickable p-1 px-2"
          onClick={onRowClick}
        >
          <span className="attribute fw-bold flex-grow-1">
            <PropertyName showIcon technicalName={category} />
          </span>
          {/* "1/40" while a value search narrows the list, so it reads as filtered. */}
          <Badge className="hiphen-badge small">
            {values.length === totalCount
              ? totalCount
              : `${values.length}/${totalCount}`}
          </Badge>
          {/* The app's outline-primary button; "active" while unfolded. */}
          <Button
            color="primary"
            outline
            size="sm"
            active={isOpen}
            className="fieldbook-edit"
            onClick={(e) => {
              e.stopPropagation();
              toggleSection(category, openedBySearch);
            }}
          >
            <i className="fa-solid fa-palette me-1" />
            Edit colors
          </Button>
        </div>

        <Collapse isOpen={isOpen}>
          <div className="d-flex flex-column gap-1 p-1">
            {isOpen &&
              (values.length > MAX_EDITABLE_VALUES ? (
                <span className="attribute text-muted p-1">
                  <i className="fa-solid fa-triangle-exclamation me-1" />
                  Too many values ({values.length}) for color editing
                </span>
              ) : (
                values.map((value) => {
                  const isNA = !value || value === NULL_GROUP_LABEL;
                  const currentColor = getAttributeColor(value);
                  const isCustomized = Boolean(
                    fieldBookColors[category]?.[value]
                  );
                  const isInherited =
                    !isCustomized &&
                    Boolean(contractColors?.[category]?.[value]);

                  return (
                    <div
                      key={value || NULL_GROUP_LABEL}
                      className="color-row d-flex align-items-center gap-2 border rounded pe-2"
                    >
                      <div
                        style={{
                          width: "12px",
                          height: "30px",
                          borderTopLeftRadius: "4px",
                          borderBottomLeftRadius: "4px",
                          backgroundColor: currentColor,
                        }}
                      />
                      <span className="attribute flex-grow-1">
                        {value ?? NULL_GROUP_LABEL}
                      </span>
                      {isInherited && (
                        <i
                          data-tooltip-id="tooltip"
                          data-tooltip-content="inherited from your company palette"
                          data-tooltip-place="top"
                          className="fa-solid fa-building discrete-icon"
                        />
                      )}
                      <ColorPicker
                        color={currentColor}
                        disabled={isNA || isSaving}
                        onChange={(color) =>
                          handleColorChange(category, value, color)
                        }
                      />
                      {isCustomized && !isNA && (
                        <button
                          type="button"
                          className="btn clickable p-0 border-0 bg-transparent"
                          onClick={() => handleRestoreDefault(category, value)}
                          disabled={isSaving}
                          title="Restore default color"
                        >
                          <i className="fa-solid fa-arrow-rotate-left" />
                        </button>
                      )}
                    </div>
                  );
                })
              ))}
          </div>
        </Collapse>
      </div>
    );
  }
);

// Keyed on the site here, once, rather than by each caller: the open sections
// and the search belong to one site's attributes.
// `onMap`: mounted next to a map of the plots (the map's Results tab, the
// breeder minimap), where a row click switches the plots to that attribute's
// colours. `onPlotMap`: the map's Plot Map tab, where a row click toggles that
// attribute's colours on the plots without leaving the tab. Without either
// (PCA) it unfolds the colours, as before.
export const TrialPreferences = ({ onMap = false, onPlotMap = false }) => {
  const trialId = useSelector(({ resultMap }) => resultMap.trial?.id);
  return (
    <TrialPreferencesEditor key={trialId} onMap={onMap} onPlotMap={onPlotMap} />
  );
};

TrialPreferences.propTypes = {
  onMap: PropTypes.bool,
  onPlotMap: PropTypes.bool,
};

const TrialPreferencesEditor = ({ onMap, onPlotMap }) => {
  const dispatch = useDispatch();
  const trial = useSelector(({ resultMap }) => resultMap.trial);
  const selectedTrait = useSelector(({ resultMap }) => resultMap.selectedTrait);
  // No results: the plots have no trait colouring, so no row to go back to.
  const hasResults = useSelector(selectHasResults);
  const mapDisplayMode = useSelector(
    ({ resultMap }) => resultMap.mapDisplayMode
  );
  const attributes = useSelector(
    selectAttributesIncludingExperimentsAndModalities
  );
  const traitsObject = useSelector(selectTraitsIndexedOnTechnicalName);
  const getPropertyCategory = useGetPropertyCategory();

  const user = useSelector(({ user }) => user);
  const isBackofficeUser = hasBackofficeAccess(user.self);
  const contract = useSelector(({ user, resultMap }) =>
    user.contracts.find(({ id }) => id === resultMap.trial?.contract_id)
  );
  const contractColors = contract?.preferences?.fieldBookColors;

  // The edits live in the store (setPendingFieldBookColors), not here: the
  // editor unmounts whenever its menu closes or its tab changes.
  const fieldBookColors = useSelector(selectEditedFieldBookColors);
  const setFieldBookColors = useCallback(
    (colors) => dispatch(setPendingFieldBookColors(colors)),
    [dispatch]
  );
  const [isSaving, setIsSaving] = useState(false);
  const [openSections, setOpenSections] = useState({});
  const [search, setSearch] = useState("");
  // Sections a value search opened and the user folded back, for this search
  // only: kept apart from openSections so folding one never changes how it
  // shows once the search is cleared.
  const [foldedBySearch, setFoldedBySearch] = useState({});

  const handleColorChange = useCallback(
    (category, value, color) =>
      setFieldBookColors({
        ...fieldBookColors,
        [category]: {
          ...fieldBookColors[category],
          [value]: color,
        },
      }),
    [fieldBookColors, setFieldBookColors]
  );

  const handleRestoreDefault = useCallback(
    (category, value) => {
      const updated = { ...fieldBookColors };
      if (updated[category]) {
        const categoryUpdated = { ...updated[category] };
        delete categoryUpdated[value];
        if (Object.keys(categoryUpdated).length === 0) delete updated[category];
        else updated[category] = categoryUpdated;
      }
      setFieldBookColors(updated);
    },
    [fieldBookColors, setFieldBookColors]
  );

  // The palette moves up to the contract instead of being copied there: the
  // site's own colours are cleared, otherwise it would keep a twin exception
  // shadowing every later edit of the company palette (9 sites follow, 1 does
  // not, and nothing on screen explains why).
  const handleSaveContractPreferences = useCallback(async () => {
    if (!trial || !contract || isSaving) return;

    setIsSaving(true);
    const updatedContract = {
      id: contract.id,
      preferences: {
        ...contract.preferences,
        fieldBookColors: mergeColors(contractColors ?? {}, fieldBookColors),
      },
    };
    const clearedSitePreferences = {
      ...trial?.preferences,
      fieldBookColors: {},
    };

    try {
      // Clear the site first: if the contract save then fails, the palette is
      // still pending locally so hasChanges stays true and the user can retry.
      // The other order leaves the site shadowing the contract with no button
      // left on screen to fix it.
      const saveSite = isBackofficeUser
        ? requestUpdateSite
        : requestUpdateSitePreferences;
      await saveSite(
        { id: trial.id, preferences: clearedSitePreferences },
        user
      );
      dispatch(setTrial({ ...trial, preferences: clearedSitePreferences }));

      const saveContract = isBackofficeUser
        ? requestUpdateContractInfo
        : requestUpdateContractPreferences;
      await saveContract(updatedContract, user);
      dispatch(
        setContractPreferences(updatedContract.id, updatedContract.preferences)
      );
      setFieldBookColors(null);
    } catch (error) {
      dispatch(addDangerAlert(error));
    } finally {
      setIsSaving(false);
    }
  }, [
    trial,
    contract,
    contractColors,
    fieldBookColors,
    user,
    dispatch,
    isSaving,
    isBackofficeUser,
    setFieldBookColors,
  ]);

  const handleSavePreferences = useCallback(async () => {
    if (!trial || isSaving) return;

    setIsSaving(true);
    const updatedSite = {
      id: trial.id,
      preferences: {
        ...trial?.preferences,
        fieldBookColors,
      },
    };

    try {
      // Backoffice and admin have no contract_infos_users row, so the client
      // endpoint would refuse them: they keep the admin scope.
      const save = isBackofficeUser
        ? requestUpdateSite
        : requestUpdateSitePreferences;
      await save(updatedSite, user);
      dispatch(
        setTrial({
          ...trial,
          preferences: {
            ...trial?.preferences,
            fieldBookColors,
          },
        })
      );
      setFieldBookColors(null);
    } catch (error) {
      dispatch(addDangerAlert(error));
    } finally {
      setIsSaving(false);
    }
  }, [
    trial,
    fieldBookColors,
    user,
    dispatch,
    isSaving,
    isBackofficeUser,
    setFieldBookColors,
  ]);

  const toggleSection = useCallback((section, openedBySearch) => {
    const toggle = (prev) => ({ ...prev, [section]: !prev[section] });
    if (openedBySearch) setFoldedBySearch(toggle);
    else setOpenSections(toggle);
  }, []);

  // Colours that differ from the saved site palette, set or restored alike.
  const changedCount = useMemo(() => {
    const saved = trial?.preferences?.fieldBookColors || {};
    return [
      ...new Set([...Object.keys(saved), ...Object.keys(fieldBookColors)]),
    ].flatMap((category) => {
      const before = saved[category] || {};
      const after = fieldBookColors[category] || {};
      return [
        ...new Set([...Object.keys(before), ...Object.keys(after)]),
      ].filter((value) => before[value] !== after[value]);
    }).length;
  }, [trial?.preferences?.fieldBookColors, fieldBookColors]);
  const hasChanges = changedCount > 0;

  // A matching attribute name keeps all its values. Otherwise the attribute
  // stays only if some values match, narrowed to them, and opens on its own:
  // searching "DAW" must not mean unfolding every attribute to find it.
  // Memoized: a colour change re-renders this component, and refiltering every
  // value would also defeat AttributeSection's memo with fresh arrays.
  const needle = search.trim().toLowerCase();
  const shownAttributes = useMemo(() => {
    const shown = Object.entries(attributes).flatMap(([category, values]) => {
      const name = traitsObject[category]?.name ?? category;
      if (!needle || name.toLowerCase().includes(needle))
        return [{ category, values, byValue: false }];
      const matching = values.filter((value) =>
        String(value ?? NULL_GROUP_LABEL)
          .toLowerCase()
          .includes(needle)
      );
      return matching.length
        ? [{ category, values: matching, byValue: true }]
        : [];
    });
    // Each open value is a colour picker: a one-letter search can match
    // hundreds of plot ids, rows and columns at once. Past this many, the
    // sections stay folded and only their "12/400" badges show the hits.
    const matchedCount = shown
      .filter(({ byValue }) => byValue)
      .reduce((sum, { values }) => sum + values.length, 0);
    const autoOpen = matchedCount <= MAX_AUTO_OPENED_VALUES;
    // Sorted here, once, not on every render (each colour change is one).
    const displayName = (key) => traitsObject[key]?.name ?? key;
    return shown
      .map(({ byValue, ...rest }) => ({
        ...rest,
        openedBySearch: byValue && autoOpen,
        priority: getPropertyCategory(rest.category).priority,
      }))
      .sort(
        (a, b) =>
          a.priority - b.priority ||
          displayName(a.category).localeCompare(displayName(b.category))
      );
  }, [attributes, needle, traitsObject, getPropertyCategory]);

  return (
    <div className="trial-preferences d-flex flex-column h-100 pt-2">
      {/* The app's search box (FilterBar, _filters.scss), not the browser's
          own search input and its native clear cross. */}
      <div className="cf-search mb-2">
        <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
        <input
          type="text"
          placeholder="Search attribute or value..."
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setFoldedBySearch({});
          }}
          aria-label="Search attribute or value"
        />
        {search && (
          <button
            type="button"
            className="cf-search__clear"
            onClick={() => {
              setSearch("");
              setFoldedBySearch({});
            }}
            aria-label="Clear search"
          >
            <i className="fa-solid fa-xmark" />
          </button>
        )}
      </div>
      <div className="flex-grow-1 overflow-auto">
        {/* The way back to the trait colours, now that Results and Field Book
            share one tab: the trait picked in the bottom bar, shown like an
            attribute row. */}
        {onMap && hasResults && selectedTrait.technical_name && (
          <div
            className={`fieldbook-category mb-1 ${
              mapDisplayMode === MAP_DISPLAY_MODES.RESULTS ? "is-displayed" : ""
            }`}
          >
            <div
              className="fieldbook-category__head d-flex align-items-center gap-2 clickable p-1 px-2"
              onClick={() =>
                dispatch(setMapDisplayMode(MAP_DISPLAY_MODES.RESULTS))
              }
            >
              <span className="attribute fw-bold flex-grow-1">
                <PropertyName
                  showIcon
                  technicalName={selectedTrait.technical_name}
                />
              </span>
            </div>
          </div>
        )}
        {shownAttributes.map(({ category, values, openedBySearch }) => (
          <AttributeSection
            key={category}
            category={category}
            values={values}
            totalCount={attributes[category].length}
            openedBySearch={openedBySearch}
            isOpen={
              openedBySearch
                ? !foldedBySearch[category]
                : openSections[category]
            }
            fieldBookColors={fieldBookColors}
            contractColors={contractColors}
            isSaving={isSaving}
            toggleSection={toggleSection}
            handleColorChange={handleColorChange}
            handleRestoreDefault={handleRestoreDefault}
            onMap={onMap}
            onPlotMap={onPlotMap}
          />
        ))}
      </div>
      {hasChanges && (
        <div className="trial-preferences__apply mt-auto">
          <span className="trial-preferences__apply-count">
            {changedCount} unsaved color{changedCount > 1 ? "s" : ""}
          </span>
          <Button
            className="hiphen-green-button"
            onClick={handleSavePreferences}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <i className="fa-solid fa-spinner fa-spin me-1" />
                Saving...
              </>
            ) : (
              <>
                <i className="fa-solid fa-floppy-disk me-1" />
                Apply to this site
              </>
            )}
          </Button>
          <Button
            outline
            onClick={handleSaveContractPreferences}
            disabled={isSaving || !contract}
            data-tooltip-id="tooltip"
            data-tooltip-content="save as your company palette: every site without its own colour follows"
            data-tooltip-place="top"
          >
            <i className="fa-solid fa-building me-1" />
            Apply to all my sites
          </Button>
        </div>
      )}
    </div>
  );
};
